
export default function UTEPBusinessPrograms() {
  const undergrad = [
    {
      title: 'BBA in Finance (Real Estate Concentration)',
      desc: 'Prepares students for careers in commercial and residential real estate, mortgage banking, property valuation, and investment analysis. Coursework covers real estate principles, property law, appraisal methods, and financial modeling specific to real property assets.',
    },
    {
      title: 'BBA in Accounting',
      desc: 'Equips graduates to sit for the CPA exam and pursue careers in public accounting, corporate finance, forensic accounting, and tax advisory. The program emphasizes ethical standards, analytical rigor, and proficiency with industry-standard software.',
    },
    {
      title: 'BBA in Management',
      desc: 'Focuses on organizational leadership, human resource management, operations, and strategic planning. Students learn to manage teams, optimize processes, and drive organizational performance in diverse business environments.',
    },
    {
      title: 'BBA in Marketing',
      desc: 'Covers consumer behavior, digital marketing, brand management, market research, and sales strategy. The bilingual El Paso market provides a natural laboratory for students to practice cross-cultural marketing techniques.',
    },
    {
      title: 'BBA in Economics',
      desc: 'Provides a rigorous foundation in micro- and macroeconomic theory, econometrics, and policy analysis. Graduates pursue careers in banking, consulting, government, and data analytics.',
    },
    {
      title: 'BBA in Information Systems',
      desc: 'Bridges business and technology by teaching database management, systems analysis, cybersecurity fundamentals, and enterprise resource planning. Graduates are in high demand as companies digitize operations.',
    },
  ];

  const graduate = [
    {
      title: 'Master of Business Administration (MBA)',
      desc: 'UTEP\u2019s MBA program offers flexible evening and online formats designed for working professionals. Concentrations include Finance, Marketing, International Business, and Management. The program emphasizes case-based learning, team projects, and real-world consulting engagements with local businesses.',
    },
    {
      title: 'MBA with Real Estate Concentration',
      desc: 'One of the few MBA programs in the region to offer a dedicated real estate track. Students study commercial property analysis, real estate development, investment portfolio management, and property law while gaining the broader strategic and financial skills of a traditional MBA.',
    },
    {
      title: 'Master of Science in Finance',
      desc: 'A specialized graduate degree for students seeking advanced careers in corporate finance, investment banking, financial analysis, and wealth management. The curriculum includes advanced financial modeling, derivatives, risk management, and portfolio theory.',
    },
    {
      title: 'Master of Accountancy (MAcc)',
      desc: 'Designed to meet the 150-hour requirement for CPA licensure in Texas. The program offers concentrations in taxation and auditing, preparing graduates for leadership roles in accounting firms, corporations, and government agencies.',
    },
    {
      title: 'Executive MBA',
      desc: 'A cohort-based program tailored for senior managers and executives with significant professional experience. The curriculum focuses on strategic leadership, organizational transformation, and global business strategy, delivered in an accelerated weekend format.',
    },
    {
      title: 'Ph.D. in Business Administration',
      desc: 'Prepares scholars for academic careers and advanced research positions. Doctoral students work closely with faculty on original research in areas such as finance, marketing, management, and information systems.',
    },
  ];

  return (
    <section className="mb-20">
      <h2 className="text-4xl font-bold text-gray-900 mb-4">Business Courses, Certificates &amp; Degrees</h2>
      <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-4xl">
        UTEP&apos;s College of Business Administration holds prestigious <strong>AACSB International accreditation</strong>, a distinction earned by fewer than six percent of business schools worldwide. This accreditation assures students, employers, and partners that the college meets the highest standards of quality in teaching, research, and curriculum design.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12">
        <div className="rounded-2xl overflow-hidden h-[400px]">
          <img
            src="https://public.readdy.ai/ai/img_res/2316535c292ee2e9ae423d9e6d606548.jpg"
            alt="UTEP College of Business Administration"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
          <p>
            The College of Business Administration serves more than 4,500 students across undergraduate, graduate, and doctoral programs. Its faculty includes nationally recognized researchers, former executives, and practicing professionals who bring both academic depth and industry relevance to the classroom. The college maintains active advisory boards composed of regional business leaders who help ensure that curricula remain aligned with employer needs and market trends.
          </p>
          <p>
            A hallmark of the college is its emphasis on experiential learning. Students participate in consulting projects with real companies, compete in national case competitions, manage a student-run investment fund, and complete internships with employers ranging from local startups to Fortune 500 corporations. These experiences give graduates a competitive edge in the job market and provide local businesses with access to motivated, well-prepared talent.
          </p>
        </div>
      </div>

      {/* Undergraduate Programs */}
      <div className="mb-14">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
            <i className="ri-graduation-cap-line text-white text-lg"></i>
          </span>
          Undergraduate Degrees (BBA)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {undergrad.map((prog, i) => (
            <div key={i} className="bg-[#FAFAF8] rounded-xl p-6 border-l-4 border-[#D2691E]">
              <h4 className="text-base font-bold text-gray-900 mb-2">{prog.title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{prog.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Graduate Programs */}
      <div className="mb-14">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
            <i className="ri-award-line text-white text-lg"></i>
          </span>
          Graduate &amp; Doctoral Programs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {graduate.map((prog, i) => (
            <div key={i} className="bg-[#FAFAF8] rounded-xl p-6 border-l-4 border-[#1B3A3A]">
              <h4 className="text-base font-bold text-gray-900 mb-2">{prog.title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{prog.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Image break */}
      <div className="rounded-2xl overflow-hidden h-[400px] w-full mb-14">
        <img
          src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20university%20business%20students%20collaborating%20around%20a%20conference%20table%20with%20laptops%20financial%20documents%20and%20presentation%20materials%20in%20a%20modern%20well%20lit%20classroom%20with%20large%20windows%20and%20a%20whiteboard%20showing%20business%20strategy%20diagrams&width=1100&height=400&seq=utepbiz008&orientation=landscape"
          alt="UTEP Business Students Collaborating"
          className="w-full h-full object-cover object-top"
        />
      </div>
    </section>
  );
}
